'use client';

import { useEffect, useState } from 'react';

import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Badge
} from 'react-bootstrap';

import {
  getShiftsReport,
  downloadShiftsCSV
} from 'features/admin/reportApi';

import Button from 'components/base/Button';

import { FontAwesomeIcon }
  from '@fortawesome/react-fontawesome';

import {
  faCalendarAlt,
  faCheckCircle,
  faClock,
  faTimesCircle,
  faSearch,
  faSpinner,
  faHospital,
  faLocationDot,
  faMoneyBillWave,
  faUsers,
  faDownload,
  faTimes,
  faCheckDouble,
  faBriefcase,
  faFileExport,
  faPoundSign,
  faRoad
} from '@fortawesome/free-solid-svg-icons';

interface Shift {
  id: number;

  pharmacy_name: string;
  pharmacy_email: string;
  contact_person: string;

  date: string;

  start_time: string;
  end_time: string;

  location: string;
  travel_expenses: string;

  pay_rate: number;

  required_locums: number;
  booked_locums_count: number;

  status: string;

  locum_role: string;
}

const ShiftReportPage = () => {

  const [loading, setLoading] =
    useState(true);

  const [exportLoading, setExportLoading] =
    useState(false);

  const [error, setError] =
    useState('');

  const [summary, setSummary] =
    useState<any>({});

  const [shifts, setShifts] =
    useState<Shift[]>([]);

  const [startDate, setStartDate] =
    useState('');

  const [endDate, setEndDate] =
    useState('');

  const [statusFilter, setStatusFilter] =
    useState('');

  const [filledFilter, setFilledFilter] =
    useState('');

  const [searchTerm, setSearchTerm] =
    useState('');

  const [page, setPage] =
    useState(1);

  const [totalPages, setTotalPages] =
    useState(1);

  const [total, setTotal] =
    useState(0);

  const itemsPerPage = 10;

  // FETCH REPORT
  const fetchReport = async (pageNumber = 1) => {
    try {

      setLoading(true);

      const filters: any = {};

      if (startDate)
        filters.start_date = startDate;

      if (endDate)
        filters.end_date = endDate;

      if (statusFilter)
        filters.status = statusFilter;

      const data =
        await getShiftsReport(filters);

      let shiftsData = data.shifts || [];

      // APPLY FILLED/UNFILLED FILTER
      if (filledFilter === 'filled') {
        shiftsData = shiftsData.filter(
          (s: Shift) =>
            s.booked_locums_count >= s.required_locums
        );
      } else if (filledFilter === 'unfilled') {
        shiftsData = shiftsData.filter(
          (s: Shift) =>
            s.booked_locums_count < s.required_locums
        );
      }

      // APPLY SEARCH FILTER
      if (searchTerm.trim()) {
        shiftsData = shiftsData.filter(
          (s: Shift) =>
            s.pharmacy_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // CALCULATE PAGINATION
      const totalRecords = shiftsData.length;
      const totalPagesCount = Math.ceil(totalRecords / itemsPerPage);
      const startIndex = (pageNumber - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedShifts = shiftsData.slice(startIndex, endIndex);

      setSummary(data.summary || {});

      setShifts(paginatedShifts);
      setPage(pageNumber);
      setTotal(totalRecords);
      setTotalPages(totalPagesCount);

    } catch (err: any) {

      setError(
        err.message ||
        'Failed to fetch reports'
      );

    } finally {

      setLoading(false);

    }
  };

  // EXPORT CSV
  const handleExportCSV = async () => {
    try {
      setExportLoading(true);

      const filters: any = {};

      if (startDate)
        filters.start_date = startDate;

      if (endDate)
        filters.end_date = endDate;

      if (statusFilter)
        filters.status = statusFilter;

      await downloadShiftsCSV(filters);

    } catch (err: any) {
      setError(
        err.message ||
        'Failed to export report'
      );
    } finally {
      setExportLoading(false);
    }
  };

  // DEFAULT DATE RANGE
  useEffect(() => {

    const today = new Date();

    const lastWeek = new Date(
      today.getTime() -
      7 * 24 * 60 * 60 * 1000
    );

    const formatDate =
      (date: Date) =>
        date.toISOString().split('T')[0];

    setStartDate(
      formatDate(lastWeek)
    );

    setEndDate(
      formatDate(today)
    );

  }, []);

  useEffect(() => {

    if (startDate && endDate) {
      fetchReport(1);
    }

  }, [startDate, endDate, filledFilter, statusFilter, searchTerm]);

  return (
    <Container
      fluid
      className='py-3'
    >

      {/* HEADER */}
      <Row className='mb-4'>
        <Col>
          <div className='d-flex flex-wrap justify-content-between align-items-start gap-3'>
            <div>
              <p className='text-body-tertiary text-uppercase fs-10 fw-bold mb-1'>
                <FontAwesomeIcon icon={faFileExport} className='me-2' />
                Admin Analytics
              </p>
              <h2 className='text-body-emphasis mb-1'>Shift Reports</h2>
              <p className='mb-0 text-body-tertiary fs-9'>
                Analyze shift activity, filled shifts, and pharmacy postings.
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* STATS CARDS */}
      <Row className='mb-4 g-3'>
        <Col md={2} lg={2.4}>
          <div className='card border-0 shadow-sm h-100'>
            <div className='card-body'>
              <div className='d-flex justify-content-between align-items-start'>
                <div>
                  <p className='text-body-tertiary fs-10 fw-bold text-uppercase mb-1'>
                    Total Shifts
                  </p>
                  <h3 className='text-body-emphasis mb-0'>{summary.total_shifts || 0}</h3>
                </div>
                <span className='avatar avatar-m text-center'>
                  <FontAwesomeIcon icon={faCalendarAlt} className='text-primary' />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={2} lg={2.4}>
          <div className='card border-0 shadow-sm h-100'>
            <div className='card-body'>
              <div className='d-flex justify-content-between align-items-start'>
                <div>
                  <p className='text-body-tertiary fs-10 fw-bold text-uppercase mb-1'>
                    Filled
                  </p>
                  <h3 className='text-success mb-0'>{summary.filled_shifts || 0}</h3>
                </div>
                <span className='avatar avatar-m text-center'>
                  <FontAwesomeIcon icon={faCheckCircle} className='text-success' />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={2} lg={2.4}>
          <div className='card border-0 shadow-sm h-100'>
            <div className='card-body'>
              <div className='d-flex justify-content-between align-items-start'>
                <div>
                  <p className='text-body-tertiary fs-10 fw-bold text-uppercase mb-1'>
                    Unfilled
                  </p>
                  <h3 className='text-warning mb-0'>{summary.unfilled_shifts || 0}</h3>
                </div>
                <span className='avatar avatar-m text-center'>
                  <FontAwesomeIcon icon={faClock} className='text-warning' />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={2} lg={2.4}>
          <div className='card border-0 shadow-sm h-100'>
            <div className='card-body'>
              <div className='d-flex justify-content-between align-items-start'>
                <div>
                  <p className='text-body-tertiary fs-10 fw-bold text-uppercase mb-1'>
                    Open
                  </p>
                  <h3 className='text-info mb-0'>{summary.open_shifts || 0}</h3>
                </div>
                <span className='avatar avatar-m text-center'>
                  <FontAwesomeIcon icon={faBriefcase} className='text-info' />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={2} lg={2.4}>
          <div className='card border-0 shadow-sm h-100'>
            <div className='card-body'>
              <div className='d-flex justify-content-between align-items-start'>
                <div>
                  <p className='text-body-tertiary fs-10 fw-bold text-uppercase mb-1'>
                    Closed
                  </p>
                  <h3 className='text-secondary mb-0'>{summary.closed_shifts || 0}</h3>
                </div>
                <span className='avatar avatar-m text-center'>
                  <FontAwesomeIcon icon={faCheckCircle} className='text-secondary' />
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ERROR ALERT */}
      {error && (
        <Row className='mb-4'>
          <Col>
            <Alert
              variant='danger'
              className='mb-0'
              onClose={() => setError('')}
              dismissible
            >
              <FontAwesomeIcon icon={faTimes} className='me-2' />
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* FILTERS AND SEARCH */}
      <Row className='mb-4 g-2'>
        <Col lg={3}>
          <label className='form-label fs-9 fw-semibold text-body-secondary mb-2'>
            Search
          </label>
          <input
            type='text'
            className='form-control'
            placeholder='Pharmacy, location...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </Col>

        <Col lg={2}>
          <label className='form-label fs-9 fw-semibold text-body-secondary mb-2'>
            Start Date
          </label>
          <input
            type='date'
            className='form-control'
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
        </Col>

        <Col lg={2}>
          <label className='form-label fs-9 fw-semibold text-body-secondary mb-2'>
            End Date
          </label>
          <input
            type='date'
            className='form-control'
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </Col>

        <Col lg={2}>
          <label className='form-label fs-9 fw-semibold text-body-secondary mb-2'>
            Status
          </label>
          <select
            className='form-select'
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value=''>All Status</option>
            <option value='open'>Open</option>
            <option value='closed'>Closed</option>
            <option value='cancelled'>Cancelled</option>
          </select>
        </Col>

        <Col lg={2}>
          <label className='form-label fs-9 fw-semibold text-body-secondary mb-2'>
            Fill Status
          </label>
          <select
            className='form-select'
            value={filledFilter}
            onChange={e => setFilledFilter(e.target.value)}
          >
            <option value=''>All</option>
            <option value='filled'>Filled</option>
            <option value='unfilled'>Unfilled</option>
          </select>
        </Col>
      </Row>

      <Row className='mb-4'>
        <Col lg={2} className='d-flex gap-2'>
          <Button
            variant='phoenix-primary'
            className='flex-grow-1'
            onClick={() => fetchReport(1)}
            startIcon={<FontAwesomeIcon icon={faSearch} />}
          >
            Search
          </Button>
        </Col>
        <Col lg={2}>
          <Button
            variant='phoenix-primary'
            onClick={handleExportCSV}
            disabled={exportLoading || shifts.length === 0}
            startIcon={
              exportLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faDownload} />
              )
            }
          >
            {exportLoading ? 'Exporting...' : 'Export CSV'}
          </Button>
        </Col>
      </Row>

      {/* LOADING STATE */}
      {loading && (
        <Row className='py-5'>
          <Col className='text-center'>
            <Spinner animation='border' variant='primary' className='mb-3' />
            <p className='text-body-tertiary fw-semibold'>
              Loading shift report...
            </p>
          </Col>
        </Row>
      )}

      {/* TABLE SECTION */}
      {!loading && (
        <Row>
          <Col>
            <div className='card border-0 shadow-sm overflow-hidden'>

              <div className='table-responsive'>

                <table className='table align-middle mb-0 fs-8'>

                  <thead className='bg-body-secondary'>

                    <tr>
                      <th
                        className='text-body-emphasis fw-bold text-uppercase ps-4 pe-3 py-3 border-0'
                        style={{ width: '20%' }}
                      >
                        Pharmacy
                      </th>

                      <th
                        className='text-body-emphasis fw-bold text-uppercase ps-3 pe-3 py-3 border-0'
                        style={{ width: '18%' }}
                      >
                        Date & Time
                      </th>

                      <th
                        className='text-body-emphasis fw-bold text-uppercase ps-3 pe-3 py-3 border-0'
                        style={{ width: '30%' }}
                      >
                        Location
                      </th>

                       <th
                        className='text-body-emphasis fw-bold text-uppercase ps-3 pe-1 py-3 border-0'
                        style={{ width: '12%' }}
                      >
                        Travel Expenses
                      </th>

                      <th
                        className='text-body-emphasis fw-bold text-uppercase ps-3 pe-3 py-3 border-0'
                        style={{ width: '10%' }}
                      >
                        Pay Rate
                      </th>

                      <th
                        className='text-body-emphasis fw-bold text-uppercase ps-3 pe-3 py-3 border-0 text-center'
                        style={{ width: '10%' }}
                      >
                        Positions
                      </th>

                      <th
                        className='text-body-emphasis fw-bold text-uppercase ps-3 pe-3 py-3 border-0 text-center'
                        style={{ width: '12%' }}
                      >
                        Status
                      </th>
                    </tr>

                  </thead>

                  <tbody>

                    {shifts.length === 0 ? (
                      <tr>

                        <td
                          colSpan={6}
                          className='text-center py-5'
                        >
                          <FontAwesomeIcon
                            icon={faBriefcase}
                            className='text-body-tertiary mb-2'
                            size='2x'
                          />
                          <p className='text-body-tertiary fw-semibold mt-2'>
                            No shifts found
                          </p>
                        </td>

                      </tr>
                    ) : (

                      shifts.map(shift => {
                        const isFilled = shift.booked_locums_count >= shift.required_locums;

                        return (
                          <tr key={shift.id} className='border-bottom border-translucent hover-row'>

                            <td className='ps-4 pe-3 py-3 border-0'>

                              <div>

                                <p className='mb-0 fw-semibold text-body-emphasis'>
                                  <FontAwesomeIcon
                                    icon={faHospital}
                                    className='me-2 text-primary'
                                  />

                                  {shift.pharmacy_name}
                                </p>

                                <small className='text-body-tertiary'>
                                  {shift.contact_person}
                                </small>

                              </div>

                            </td>

                            <td className='ps-3 pe-3 py-3 border-0'>

                              <div className='fs-9'>
                                <p className='mb-1 fw-semibold'>
                                  <FontAwesomeIcon
                                    icon={faCalendarAlt}
                                    className='me-2 text-info'
                                  />
                                  {new Date(
                                    shift.date
                                  ).toLocaleDateString(
                                    'en-GB'
                                  )}
                                </p>

                                <small className='text-body-tertiary'>
                                  <FontAwesomeIcon
                                    icon={faClock}
                                    className='me-2'
                                  />
                                  {shift.start_time} - {shift.end_time}
                                </small>
                              </div>

                            </td>

                            <td className='ps-3 pe-3 py-3 border-0'>

                              <FontAwesomeIcon
                                icon={faLocationDot}
                                className='me-2 text-danger'
                              />

                              {shift.location}

                            </td>

                            <td className='ps-3 pe-3 py-3 border-0'>
                              {shift.travel_expenses ? `${shift.travel_expenses}` : 'N/A'  }
                            </td>

                            <td className='ps-3 pe-3 py-3 border-0'>

                              <div className='fs-9 fw-bold'>
                                <FontAwesomeIcon
                                  icon={faPoundSign}
                                  className='me-2'
                                />

                                {shift.pay_rate}

                              </div>

                            </td>

                            <td className='ps-3 pe-3 py-3 border-0 text-center'>

                              <div className='fs-9'>
                                <Badge bg='primary' className='me-2'>
                                  <FontAwesomeIcon icon={faUsers} className='me-1' />
                                  {shift.booked_locums_count}/{shift.required_locums}
                                </Badge>
                              </div>

                            </td>

                            <td className='ps-3 pe-3 py-3 border-0 text-center'>

                              <div className='d-flex gap-2 justify-content-center'>
                                <Badge
                                  bg={
                                    isFilled
                                      ? 'success'
                                      : 'warning'
                                  }
                                  className='rounded-pill fw-semibold'
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      isFilled
                                        ? faCheckDouble
                                        : faClock
                                    }
                                    className='me-1'
                                  />
                                  {isFilled ? 'Filled' : 'Unfilled'}
                                </Badge>

                                <Badge
                                  bg={
                                    shift.status === 'open'
                                      ? 'info'
                                      : shift.status === 'closed'
                                        ? 'secondary'
                                        : 'danger'
                                  }
                                  className='rounded-pill fw-semibold'
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      shift.status === 'open'
                                        ? faBriefcase
                                        : shift.status === 'closed'
                                          ? faCheckCircle
                                          : faTimesCircle
                                    }
                                    className='me-1'
                                  />
                                  {shift.status.charAt(0).toUpperCase() + shift.status.slice(1)}
                                </Badge>
                              </div>

                            </td>

                          </tr>
                        );
                      })
                    )}

                  </tbody>

                </table>

              </div>

              {/* PAGINATION */}
              {!loading && shifts.length > 0 && (
                <div className='card-footer bg-body border-top border-translucent py-3'>
                  <ul className='pagination justify-content-center mb-0 gap-1'>
                    {page > 1 && (
                      <li className='page-item'>
                        <button
                          className='page-link'
                          onClick={() => fetchReport(page - 1)}
                        >
                          Previous
                        </button>
                      </li>
                    )}

                    {[...Array(totalPages)].map((_, i) => (
                      <li
                        key={i}
                        className={`page-item ${page === i + 1 ? 'active' : ''
                          }`}
                      >
                        <button
                          className='page-link'
                          onClick={() => fetchReport(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))}

                    {page < totalPages && (
                      <li className='page-item'>
                        <button
                          className='page-link'
                          onClick={() => fetchReport(page + 1)}
                        >
                          Next
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}

            </div>
          </Col>
        </Row>
      )}

      <style>{`
        .hover-row:hover {
          background-color: rgba(0, 0, 0, 0.02);
          transition: background-color 0.2s ease;
        }
      `}</style>

    </Container>
  );
};

export default ShiftReportPage;